import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';

const RespondLandingPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDisputeData = async () => {
      try {
        const data = await api.getRespondView(token);
        setDispute(data);
      } catch (err) {
        setError(err.message || 'Failed to load dispute information');
      } finally {
        setLoading(false);
      }
    };

    fetchDisputeData();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dispute information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Unable to Load Dispute</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              This link may have expired or is no longer valid. Please contact the sender for a new invitation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleReady = () => {
    navigate(`/respond/${token}/prepare`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="text-5xl mb-4">🤝</div>
          <h1 className="text-3xl font-bold mb-3">
            {dispute?.initiatorName || 'Someone'} has requested a discussion
          </h1>
          <p className="text-xl text-gray-600">
            About: <span className="font-semibold">{dispute?.disputeType || 'a dispute'}</span>
          </p>
        </div>

        <div className="card mb-8">
          <div className="mb-6">
            <h2 className="text-xl font-bold mb-3">This is an invitation, not a summons</h2>
            <p className="text-gray-700 mb-4">
              This is an opportunity to resolve matters privately through a structured process
              before any formal legal proceedings.
            </p>
            <p className="text-gray-700">
              The process is supervised by licensed attorneys and designed to find fair outcomes
              based on similar cases.
            </p>
          </div>

          <div className="bg-gray-100 rounded-lg p-8 mb-6 text-center border-2 border-dashed border-gray-300">
            <div className="text-4xl mb-2">🎥</div>
            <p className="text-sm text-gray-600">90-Second Attorney Explanation Video</p>
            <p className="text-xs text-gray-500 mt-2">(Video will be embedded here)</p>
          </div>

          <div className="bg-blue-50 rounded-lg p-6 mb-6">
            <h3 className="font-bold mb-3">What you should know:</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">✓</span>
                <span>Your responses remain private unless a settlement is reached</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">✓</span>
                <span>The process typically takes 72 hours from start to finish</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">✓</span>
                <span>An attorney reviews all agreements before finalization</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">✓</span>
                <span>If no agreement is reached, nothing you shared can be used in court</span>
              </li>
            </ul>
          </div>

          {dispute && (
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="font-bold mb-3">Dispute Details:</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Type:</span>
                  <p className="font-medium">{dispute.disputeType}</p>
                </div>
                <div>
                  <span className="text-gray-500">Amount Claimed:</span>
                  <p className="font-medium">€{dispute.amountClaimed?.toLocaleString()}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-500">Filed:</span>
                  <p className="font-medium">{new Date(dispute.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleReady}
              className="btn-primary flex-1"
            >
              🤝 I'm Ready to Proceed
            </button>
            <button
              onClick={() => window.open('https://tensionbridge.pages.dev/help', '_blank')}
              className="btn-secondary flex-1"
            >
              📚 Learn More
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              className="text-sm text-gray-600 hover:text-gray-800"
              onClick={() => alert('You can respond within 48 hours. We will send you a reminder if needed.')}
            >
              ⏰ I Need Time to Think
            </button>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>
            🔒 Confidential • 🛡️ Private • ⚖️ Attorney-Supervised
          </p>
          <p className="mt-2">
            Questions? Contact support@tensionbridge.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default RespondLandingPage;
